import { useMemo } from 'react';
import { useSharedData } from '@/hooks/useSharedData';
import { Employee } from '@/services/employee.service';

interface DashboardStats {
  employees: {
    total: number;
    active: number;
    onLeave: number;
    newThisMonth: number;
  };
  candidates: {
    total: number;
    new: number;
    interview: number;
    hired: number;
    rejected: number;
  };
  jobs: {
    total: number;
    active: number;
    closed: number;
  };
  interviews: {
    total: number;
    scheduled: number;
    completed: number;
    pending: number;
  };
  onboarding: {
    total: number;
    inProgress: number;
    completed: number;
  };
  departments: Array<{
    name: string;
    count: number;
    percentage: number;
  }>;
}

export const useDashboardStats = (): DashboardStats => {
  const { employees, candidates, jobs, interviews, onboardings } = useSharedData();

  // Helper function to get status from different formats
  const getStatus = (item: any): string => {
    if (typeof item.status === 'string') {
      return item.status.toLowerCase();
    } else if (item.status && typeof item.status === 'object') {
      const status = item.status.name || item.status.status || 'unknown';
      return status.toLowerCase();
    }
    return 'unknown';
  };

  return useMemo((): DashboardStats => {
    // Process employees
    const activeEmployees = employees.filter((emp: Employee) => {
      const status = getStatus(emp);
      return status === 'active' || status === 'employed';
    });

    const onLeaveEmployees = employees.filter((emp: Employee) => {
      const status = getStatus(emp);
      return status === 'on leave' || status === 'leave';
    });

    // Calculate new employees this month
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const newThisMonth = employees.filter((emp: Employee) => {
      if (!emp.joinDate && !emp.joiningDate) return false;
      const joinDate = new Date(emp.joinDate || emp.joiningDate || '');
      return joinDate.getMonth() === currentMonth && joinDate.getFullYear() === currentYear;
    }).length;

    // Process candidates
    const candidateStats = {
      total: candidates.length,
      new: candidates.filter((c: any) => {
        const status = getStatus(c);
        return status === 'new' || status === 'applied' || status === 'pending';
      }).length,
      interview: candidates.filter((c: any) => {
        const status = getStatus(c);
        return status === 'interview_scheduled';
      }).length,
      hired: candidates.filter((c: any) => {
        const status = getStatus(c);
        return status === 'hired' || status === 'selected' || status === 'accepted';
      }).length,
      rejected: candidates.filter((c: any) => {
        const status = getStatus(c);
        return status === 'rejected' || status === 'declined' || status === 'not_selected';
      }).length
    };

    // Process jobs
    const activeJobs = jobs.filter((job: any) => {
      const status = getStatus(job);
      return job.isActive === true && status !== 'closed' && status !== 'cancelled';
    });

    const closedJobs = jobs.filter((job: any) => {
      const status = getStatus(job);
      return status === 'closed';
    });

    // Process interviews
    const interviewStats = {
      total: interviews.length,
      scheduled: interviews.filter((i: any) => {
        const status = getStatus(i);
        return status === 'scheduled' || status === 'upcoming' || status === 'confirmed';
      }).length,
      completed: interviews.filter((i: any) => {
        const status = getStatus(i);
        return status === 'completed' || status === 'finished' || status === 'done';
      }).length,
      pending: interviews.filter((i: any) => {
        const status = getStatus(i);
        return status === 'pending' || status === 'waiting' || status === 'to_be_scheduled';
      }).length
    };

    // Process onboardings
    const onboardingStats = {
      total: onboardings.length,
      inProgress: onboardings.filter((o: any) => o.status === 'in_progress').length,
      completed: onboardings.filter((o: any) => o.status === 'completed').length
    };

    // Calculate department stats
    const departmentCounts: { [key: string]: number } = {};
    employees.forEach((emp: Employee) => {
      const deptName = typeof emp.department === 'string' ? emp.department : emp.department?.name || 'Unknown';
      departmentCounts[deptName] = (departmentCounts[deptName] || 0) + 1;
    });

    const departmentStats = Object.entries(departmentCounts).map(([name, count]) => ({
      name,
      count,
      percentage: Math.round((count / employees.length) * 100)
    }));

    return {
      employees: {
        total: employees.length,
        active: activeEmployees.length,
        onLeave: onLeaveEmployees.length,
        newThisMonth
      },
      candidates: candidateStats,
      jobs: {
        total: jobs.length,
        active: activeJobs.length,
        closed: closedJobs.length
      },
      interviews: interviewStats,
      onboarding: onboardingStats,
      departments: departmentStats
    };
  }, [employees, candidates, jobs, interviews, onboardings]);
}; 